import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api, fmtEURPrecise, fmtDateDE } from "../lib/api";
import useSEO from "../hooks/useSEO";

export default function PaymentResult({ success }) {
  useSEO({
    title: success ? "Buchung bestätigt · Caravan Reisen" : "Buchung abgebrochen · Caravan Reisen",
    description: success ? "Deine Reisemobil-Buchung wurde erfolgreich bezahlt und bestätigt." : "Deine Buchung wurde nicht abgeschlossen. Du kannst es jederzeit erneut versuchen.",
    path: success ? "/buchung-erfolg" : "/buchung-abbruch",
    noindex: true,
  });

  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const bookingId = params.get("booking_id");
  const [status, setStatus] = useState("polling");
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    if (!success) { setStatus("cancelled"); return; }
    if (!sessionId) { setStatus("error"); return; }

    let tries = 0;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          if (bookingId) {
            const b = await api.get(`/bookings/${bookingId}`);
            setBooking(b.data);
          }
          return;
        }
        if (["failed", "expired"].includes(r.data.payment_status)) {
          setStatus("failed");
          return;
        }
      } catch (e) {}
      if (tries++ < 15) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId, success, bookingId]);

  return (
    <div className="pt-40 pb-24 max-w-2xl mx-auto px-6" data-testid="payment-result-page">
      <div className="bg-white rounded-3xl border border-[#D8D6D0] p-10 md:p-14 text-center">
        {status === "polling" && (
          <>
            <Loader2 size={48} className="mx-auto animate-spin text-[#C05C42]" />
            <h1 className="section-title mt-6">Zahlung wird geprüft...</h1>
            <p className="mt-3 text-[#1B2A22]/70">Bitte einen Moment Geduld.</p>
          </>
        )}

        {status === "paid" && (
          <>
            <CheckCircle2 size={56} className="mx-auto text-[#A9BA9D]" />
            <h1 className="section-title mt-6" data-testid="payment-success-heading">Buchung bestätigt!</h1>
            <p className="mt-3 text-[#1B2A22]/70">Vielen Dank für deine Buchung. Du erhältst in Kürze eine Bestätigungs-E-Mail.</p>
            {booking && (
              <div className="mt-8 text-left bg-[#F0EFEA] rounded-xl p-6 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-[#1B2A22]/60">Buchungsnummer</span><span className="font-mono">{booking.id.slice(0, 8).toUpperCase()}</span></div>
                <div className="flex justify-between"><span className="text-[#1B2A22]/60">Fahrzeug</span><span className="font-semibold">{booking.vehicle_name}</span></div>
                <div className="flex justify-between"><span className="text-[#1B2A22]/60">Zeitraum</span><span>{fmtDateDE(booking.start_date)} – {fmtDateDE(booking.end_date)}</span></div>
                <div className="flex justify-between"><span className="text-[#1B2A22]/60">Nächte</span><span>{booking.nights}</span></div>
                <div className="flex justify-between pt-2 border-t border-[#D8D6D0] font-bold"><span>Bezahlt</span><span>{fmtEURPrecise(booking.total)}</span></div>
              </div>
            )}
            <Link to="/" className="btn-primary mt-8 justify-center inline-flex" data-testid="back-home">Zur Startseite</Link>
          </>
        )}

        {(status === "cancelled" || status === "failed" || status === "timeout" || status === "error") && (
          <>
            <XCircle size={56} className="mx-auto text-[#C05C42]" />
            <h1 className="section-title mt-6">{status === "cancelled" ? "Zahlung abgebrochen" : "Zahlung fehlgeschlagen"}</h1>
            <p className="mt-3 text-[#1B2A22]/70">
              {status === "cancelled" ? "Deine Buchung wurde nicht abgeschlossen. Du kannst es jederzeit erneut versuchen." : "Leider konnte die Zahlung nicht abgeschlossen werden."}
            </p>
            <Link to="/fahrzeuge" className="btn-primary mt-8 justify-center inline-flex">Erneut buchen</Link>
          </>
        )}
      </div>
    </div>
  );
}
